/* eslint-disable no-shadow */
import {
	Dispatch,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { NotUndefined, sha1 } from "object-hash";
// eslint-disable-next-line import/no-unresolved
import { ModelEventMeta } from "@node-elion/syncron";
import { isEqual } from "lodash";
import { useDebouncedCallback } from "use-debounce";

import { useTypedSelector } from "../redux/store";

import useObjectEditor from "./useObjectEditor";

declare namespace useModelSubscribe {
	interface ServiceSubscribeOnUpdateData<Model> {
		models: Model[];
		metadataState: ModelEventMeta;
	}

	interface Data<Model> extends ServiceSubscribeOnUpdateData<Model> {
		loading: boolean;
	}

	type OnUpdate<Model> = (
		data: ServiceSubscribeOnUpdateData<Model>,
	) => void | Promise<void>;

	interface Subscription<Options> {
		unsubscribe(): Promise<void>;
		update(options: Options): Promise<void>;
	}

	interface Service<Options, Model> {
		subscribe(
			options: Options,
			onUpdate: OnUpdate<Model>,
		): Promise<Subscription<Options> | null>;
	}

	interface Options<Model> {
		isAuth?: boolean;
		debounce?: number;
		onUpdate?: Dispatch<useModelSubscribe.Data<Model>>;
		onUpdateModels?: Dispatch<useModelSubscribe.Data<Model>["models"]>;
	}
}

function useModelSubscribe<SubscribeOptions extends NotUndefined, Model>(
	subscribeOptions: SubscribeOptions,
	Service: useModelSubscribe.Service<SubscribeOptions, Model>,
	options: useModelSubscribe.Options<Model> = {
		debounce: 300,
		isAuth: false,
	},
): useModelSubscribe.Data<Model> {
	const connected = useTypedSelector((state) => state.prpc.connected);
	const authorized = useTypedSelector((state) => state.prpc.authorized);
	const subscriptionRef =
		useRef<useModelSubscribe.Subscription<SubscribeOptions> | null>(null);

	const ServiceRef = useRef<useModelSubscribe.Service<
		SubscribeOptions,
		Model
	> | null>(null);

	const subscribeOptionsRef = useRef<SubscribeOptions | null>(null);

	const currentSubscribePromiseRef =
		useRef<
			Promise<useModelSubscribe.Subscription<SubscribeOptions> | null>
		>();

	const [data, setData] = useState<useModelSubscribe.Data<Model>>({
		loading: false,
		models: [],
		metadataState: {},
	});

	const [reLoad, setReload] = useState<boolean>(false);

	const isConnectRef = useRef<boolean>(!options?.isAuth);

	const isMountedRef = useRef<boolean>(true);

	const dataEditor = useObjectEditor(data, setData);

	const subscribeOptionsHash = sha1(subscribeOptions);

	const lastSubscribeOptionsHashRef = useRef<string | null>(null);

	const optionsRef = useRef(options);
	optionsRef.current = options;

	const update = useCallback(async () => {
		if (ServiceRef?.current !== Service) {
			ServiceRef.current = Service;
			subscribeOptionsRef.current = subscribeOptions;

			const subscription = subscriptionRef?.current;
			subscriptionRef.current = null;

			if (subscription) subscription?.unsubscribe?.();

			const subscribePromise = Service?.subscribe(
				subscribeOptions,
				(newData) => {
					// The server keeps streaming until it processes our unsubscribe,
					// so late batches must not reach the store after unmount.
					if (!isMountedRef.current) return;

					const updatedData: useModelSubscribe.Data<Model> = {
						models: newData?.models || [],
						metadataState: newData?.metadataState || {},
						loading: dataEditor.get("loading"),
					};

					setData(updatedData);
					setReload(true);

					// Parking / taxiService / orders callbacks dispatch to
					// Redux. Doing that in this WS turn can land mid-render of
					// a sibling and drop its pending layout effect, so the
					// live orders tab keeps a stale store after remount.
					queueMicrotask(() => {
						if (!isMountedRef.current) return;
						optionsRef.current?.onUpdateModels?.(
							updatedData.models,
						);
						optionsRef.current?.onUpdate?.(updatedData);
					});
				},
			);

			currentSubscribePromiseRef.current = subscribePromise;

			const newSubscription = await subscribePromise;

			if (currentSubscribePromiseRef.current === subscribePromise) {
				subscriptionRef.current = newSubscription;

				const previousHash = lastSubscribeOptionsHashRef.current;
				lastSubscribeOptionsHashRef.current = subscribeOptionsHash;

				if (
					previousHash !== null &&
					previousHash !== subscribeOptionsHash
				) {
					dataEditor.set("loading", true);
					await subscriptionRef?.current?.update?.(
						subscribeOptionsRef.current,
					);
					dataEditor.set("loading", false);
				}
			} else newSubscription?.unsubscribe?.();
		} else if (!isEqual(subscribeOptionsRef.current, subscribeOptions)) {
			subscribeOptionsRef.current = subscribeOptions;

			const subscription = subscriptionRef.current;

			dataEditor.set("loading", true);
			if (subscription?.update)
				await subscription?.update?.(subscribeOptions);

			lastSubscribeOptionsHashRef.current = subscribeOptionsHash;

			dataEditor.set("loading", false);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [Service, subscribeOptionsHash]);

	const debouncedUpdate = useDebouncedCallback(update, options.debounce);

	useEffect(() => {
		if (options?.isAuth) {
			if (isConnectRef?.current) debouncedUpdate();
		} else {
			debouncedUpdate();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [Service, subscribeOptionsHash, isConnectRef?.current, options?.isAuth]);

	useEffect(() => {
		if (options?.isAuth) {
			isConnectRef.current = connected && authorized;
		}
	}, [connected, authorized, options?.isAuth]);

	useEffect(() => {
		isMountedRef.current = true;

		return () => {
			isMountedRef.current = false;
			ServiceRef.current = null;
			subscriptionRef.current = null;
			lastSubscribeOptionsHashRef.current = null;

			(async () => {
				(await currentSubscribePromiseRef?.current)?.unsubscribe?.();
			})();
		};
	}, []);

	const isReload = useMemo(() => {
		if (!reLoad) return true;
		return true;
	}, [reLoad]);

	return useMemo(() => {
		setReload(false);
		return data;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isReload, data]);
}

export default useModelSubscribe;
